import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Grid,
  CardMedia,
  LinearProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  WorkOutline as WorkIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeletePhotoIcon,
  PhotoCamera as PhotoCameraIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import api from '../services/api';
import { downloadFileAsBlob } from '../services/fileService';
import { agencySendQuote, agencyCounterQuote, agencyAcceptQuote, agencyDeclineQuote, deleteJob } from '../services/contractorJobService';
import { QuoteManagement } from './QuoteManagement';
import { JobWithResponses } from './JobWithResponses';
import { 
  acceptContractorQuote, 
  declineContractorQuote, 
  getPropertyQuotes,
  type JobQuote 
} from '../services/quoteManagementService';
import { useAuth } from '../hooks/useAuth';
import openScheduleService from '../services/openScheduleService';
import contractorAvailabilityService from '../services/contractorAvailabilityService';

interface ContractorJob {
  _id: string;
  accessToken: string;
  contractorName: string;
  contractorEmail: string;
  contractorType: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
  messages?: any[];
  attachments?: any[];
  invoices?: any[];
}

interface Contractor {
  _id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  contractorTypeSlug: string;
  contractorTypeName: string;
  contractorTypeIcon: string;
  isActive: boolean;
}

interface PropertyContractorJobsProps {
  propertyId: string;
  agencySlug: string;
  property: any;
  initialNewJobType?: string;
  initialSendQuotes?: boolean;
}

const PropertyContractorJobs: React.FC<PropertyContractorJobsProps> = ({
  propertyId,
  agencySlug,
  property,
  initialNewJobType,
  initialSendQuotes
}) => {
  const { user } = useAuth();
  const [contractorJobs, setContractorJobs] = useState<ContractorJob[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [contractorTypes, setContractorTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false); // legacy; no longer used for loading
  const [createJobDialogOpen, setCreateJobDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const [existingPhotos, setExistingPhotos] = useState<Array<{ _id?: string; url?: string; filename?: string }>>([]);
  const [removedPhotoIds, setRemovedPhotoIds] = useState<string[]>([]);
  const [selectedJobType, setSelectedJobType] = useState('');
  const [jobNotes, setJobNotes] = useState('');
  const [jobStartDate, setJobStartDate] = useState('');
  const [jobFinishDate, setJobFinishDate] = useState('');
  const [jobStartTime, setJobStartTime] = useState('');
  const [jobFinishTime, setJobFinishTime] = useState('');
  const [openSchedule, setOpenSchedule] = useState<any>(null);
  const [sendQuoteDialogOpen, setSendQuoteDialogOpen] = useState(false);
  const [selectedJobForQuote, setSelectedJobForQuote] = useState<ContractorJob | null>(null);
  const [selectedContractors, setSelectedContractors] = useState<string[]>([]);
  const [sendQuoteAmount, setSendQuoteAmount] = useState<string>('');
  const [sendQuoteNotes, setSendQuoteNotes] = useState<string>('');
  const [jobPhotos, setJobPhotos] = useState<File[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  // State for sending quotes when creating job
  const [sendQuotesOnCreate, setSendQuotesOnCreate] = useState(false);
  const [createJobContractors, setCreateJobContractors] = useState<string[]>([]); // Keep as array for compatibility, but only allow one selection
  const [createJobQuoteAmount, setCreateJobQuoteAmount] = useState<string>('');
  const [createJobQuoteNotes, setCreateJobQuoteNotes] = useState<string>('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ContractorJob | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{ title: string; message: string; confirmText: string; confirmColor?: 'primary' | 'error' | 'success' } | null>(null);
  const [pendingCancelJob, setPendingCancelJob] = useState<ContractorJob | null>(null);
  // Quote dialog state
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteJob, setQuoteJob] = useState<ContractorJob | null>(null);
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  
  // Quote management state
  const [propertyQuotes, setPropertyQuotes] = useState<JobQuote[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  
  // Add contractor dialog state
  const [addContractorDialogOpen, setAddContractorDialogOpen] = useState(false);
  const [newContractorData, setNewContractorData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: ''
  });
  const [creatingContractor, setCreatingContractor] = useState(false);
  
  // Availability state
  const [contractorAvailability, setContractorAvailability] = useState<Record<string, any[]>>({});
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [selectedAvailabilitySlot, setSelectedAvailabilitySlot] = useState<{ date: string; time: string } | null>(null);

  const isAdmin = user?.primaryRole === 'admin';

  // Build job type options from active contractor types, keep alphabetical order
  const jobTypeOptions = React.useMemo(() => {
    const arr = contractorTypes
      .filter(t => t.isActive)
      .map(t => ({ value: t.slug, label: t.name }));
    arr.sort((a, b) => a.label.localeCompare(b.label));
    return arr;
  }, [contractorTypes]);

  const toInputTime = (d?: string) => {
    if (!d) return '';
    try {
      const date = new Date(d);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  const toInputDate = (d?: string) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return '';
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return '';
    }
  };
  // If parent requests opening create dialog with a specific type, honor it once
  const initialTypeHandledRef = React.useRef(false);
  const chooseTypeForIncoming = (incomingRaw: string, options: Array<{ value: string; label: string }>) => {
    const normalize = (s: string) => s.toLowerCase().trim().replace(/%2f/gi, '/').replace(/[^a-z0-9]/g, '');
    let incoming = incomingRaw;
    try { incoming = decodeURIComponent(incoming); } catch {}
    const norm = normalize(incoming);
    // Special case: "true" means just open the dialog without pre-selecting
    if (norm === 'true') {
      return '';
    }
    // 1) exact match on slug or label
    let found = options.find(o => normalize(o.value) === norm || normalize(o.label) === norm);
    if (found) return found.value;
    // 2) substring match on label or slug
    found = options.find(o => normalize(o.value).includes(norm) || normalize(o.label).includes(norm));
    if (found) return found.value;
    // 3) synonyms for styling
    const stylingNorms = ['stylist', 'styling', 'styler', 'stager', 'style'];
    if (stylingNorms.includes(norm)) {
      found = options.find(o => {
        const v = normalize(o.value); const l = normalize(o.label);
        return v.includes('styl') || l.includes('styl') || v.includes('stager') || l.includes('stager');
      });
      if (found) return found.value;
    }
    // 4) synonyms for sign board maker
    const signBoardNorms = ['signboard', 'signboardmaker', 'signboardmaker', 'sign', 'board', 'signmaker'];
    if (signBoardNorms.includes(norm)) {
      found = options.find(o => {
        const v = normalize(o.value);
        return v.includes('sign') && (v.includes('board') || v.includes('maker'));
      });
      if (found) return found.value;
    }
    return options[0]?.value;
  };

  useEffect(() => {
    if (!initialNewJobType || initialTypeHandledRef.current) return;
    if (jobTypeOptions.length === 0) return; // wait until options are loaded
    const chosen = chooseTypeForIncoming(initialNewJobType, jobTypeOptions);
    setSelectedJobType(chosen);
    setEditMode(false);
    setEditingJobId(null);
    setExistingPhotos([]);
    setRemovedPhotoIds([]);
    // Enable send quotes checkbox if initialSendQuotes is true
    if (initialSendQuotes) {
      setSendQuotesOnCreate(true);
    }
    setCreateJobDialogOpen(true);
    initialTypeHandledRef.current = true;
  }, [initialNewJobType, initialSendQuotes, jobTypeOptions]);

  // Fetch open schedule when auctioner job type is selected
  useEffect(() => {
    if (selectedJobType === 'auctioner' && propertyId && agencySlug) {
      const fetchSchedule = async () => {
        try {
          const scheduleData = await openScheduleService.getOpenSchedule(propertyId, agencySlug);
          setOpenSchedule(scheduleData);
          
          // Prefill date and time from auction schedule if available
          if (scheduleData?.auctionDate && scheduleData?.auctionTime) {
            // Format date as YYYY-MM-DD
            const auctionDate = new Date(scheduleData.auctionDate);
            if (!isNaN(auctionDate.getTime())) {
              const year = auctionDate.getFullYear();
              const month = String(auctionDate.getMonth() + 1).padStart(2, '0');
              const day = String(auctionDate.getDate()).padStart(2, '0');
              setJobStartDate(`${year}-${month}-${day}`);
              setJobFinishDate(`${year}-${month}-${day}`);
            }
            
            // Set time (auctionTime is in HH:MM format)
            if (scheduleData.auctionTime) {
              setJobStartTime(scheduleData.auctionTime);
              // For finish time, add 30 minutes to start time
              const [hours, minutes] = scheduleData.auctionTime.split(':').map(Number);
              const finishTime = new Date();
              finishTime.setHours(hours, minutes + 30, 0, 0);
              const finishHours = String(finishTime.getHours()).padStart(2, '0');
              const finishMinutes = String(finishTime.getMinutes()).padStart(2, '0');
              setJobFinishTime(`${finishHours}:${finishMinutes}`);
            }
          }
        } catch (err: any) {
          if (err.response?.status !== 404) {
            console.error('Error fetching open schedule:', err);
          }
          // Don't set error if schedule doesn't exist - it's optional
        }
      };
      fetchSchedule();
    } else if (selectedJobType !== 'auctioner') {
      // Clear time fields when switching away from auctioner
      setJobStartTime('');
      setJobFinishTime('');
      setOpenSchedule(null);
    }
  }, [selectedJobType, propertyId, agencySlug]);

  // Open Create Job dialog when requested from other components
  useEffect(() => {
    const handleOpenCreate = (e: any) => {
      let preferred = e?.detail?.type || 'handyman';
      const chosen = jobTypeOptions.length > 0 ? chooseTypeForIncoming(preferred, jobTypeOptions) : preferred;
      setSelectedJobType(chosen);
      setEditMode(false);
      setEditingJobId(null);
      setExistingPhotos([]);
      setRemovedPhotoIds([]);
      setCreateJobDialogOpen(true);
    };
    window.addEventListener('contractorJob:create', handleOpenCreate as any);
    return () => window.removeEventListener('contractorJob:create', handleOpenCreate as any);
  }, [jobTypeOptions]);

  // Ensure selected type is valid when options change
  useEffect(() => {
    if (initialTypeHandledRef.current) return;
    if (!selectedJobType && jobTypeOptions.length > 0) {
      const preferred = jobTypeOptions.find(o => o.value.toLowerCase().includes('handyman'))?.value || jobTypeOptions[0].value;
      setSelectedJobType(preferred);
    } else if (selectedJobType && jobTypeOptions.length > 0 && !jobTypeOptions.some(o => o.value === selectedJobType)) {
      setSelectedJobType(jobTypeOptions[0].value);
    }
  }, [jobTypeOptions]);

  // Quote management functions
  const fetchPropertyQuotes = async () => {
    try {
      setQuotesLoading(true);
      const quotes = await getPropertyQuotes(agencySlug, propertyId);
      setPropertyQuotes(quotes);
    } catch (error) {
      console.error('Error fetching property quotes:', error);
      setSnackbar({ open: true, message: 'Failed to load quotes', severity: 'error' });
    } finally {
      setQuotesLoading(false);
    }
  };

  // Load quotes when the section expands so QuoteManagement can display if available
  useEffect(() => {
    if (expanded) {
      fetchPropertyQuotes();
    }
  }, [expanded]);

  const handleAcceptQuote = async (jobId: string, contractorId: string) => {
    try {
      await acceptContractorQuote(agencySlug, jobId, contractorId);
      await fetchPropertyQuotes();
      await fetchContractorJobs();
      setSnackbar({ open: true, message: 'Quote approved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error accepting quote:', error);
      setSnackbar({ open: true, message: 'Failed to approve quote', severity: 'error' });
    }
  };

  const handleDeclineQuote = async (jobId: string, contractorId: string) => {
    try {
      await declineContractorQuote(agencySlug, jobId, contractorId);
      await fetchPropertyQuotes();
      await fetchContractorJobs();
      setSnackbar({ open: true, message: 'Quote declined successfully', severity: 'success' });
    } catch (error) {
      console.error('Error declining quote:', error);
      setSnackbar({ open: true, message: 'Failed to decline quote', severity: 'error' });
    }
  };

  const handleViewQuoteDetails = (jobId: string, contractorId: string) => {
    // This could open a detailed view of the quote
    console.log('View quote details:', jobId, contractorId);
  };

  const openSendQuotesDialog = (job: ContractorJob) => {
    setSelectedJobForQuote(job);
    setSelectedContractors([]);
    setSendQuoteDialogOpen(true);
  };

  const openDeleteJobConfirm = (job: ContractorJob) => {
    setPendingCancelJob(job);
    setConfirmDialogConfig({
      title: 'Delete Contractor Job',
      message: `Are you sure you want to permanently delete the job for ${job.contractorName}? This action cannot be undone and will remove all associated files.`,
      confirmText: 'Delete Job',
      confirmColor: 'error'
    });
    setConfirmDialogOpen(true);
  };

  const handleDeleteJob = async (job: any) => {
    openDeleteJobConfirm(job);
  };

  const fetchContractorJobs = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
      setContractorJobs(response.data);
    } catch (err: any) {
      console.error('Error fetching contractor jobs:', err);
      setError('Failed to load contractor jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchContractors = async () => {
    try {
      const response = await api.get(`/agencies/${agencySlug}/contractors`);
      setContractors(response.data.filter((c: Contractor) => c.isActive));
    } catch (err: any) {
      console.error('Error fetching contractors:', err);
    }
  };

  const fetchContractorTypes = async () => {
    try {
      const response = await api.get(`/agencies/${agencySlug}/contractor-types`);
      setContractorTypes(response.data);
    } catch (err: any) {
      console.error('Error fetching contractor types:', err);
    }
  };

  useEffect(() => {
    if (agencySlug && propertyId) {
      fetchContractorJobs();
      fetchContractors();
      fetchContractorTypes();
    }
  }, [propertyId, agencySlug]);

  // Ensure contractors are loaded when opening Send Quotes dialog
  useEffect(() => {
    if (sendQuoteDialogOpen && contractors.length === 0) {
      fetchContractors();
    }
  }, [sendQuoteDialogOpen]);

  // Fetch availability for contractors when dialog opens
  useEffect(() => {
    const fetchAvailabilityForContractors = async () => {
      if (contractors.length > 0) {
        let contractorTypeToUse: string | null = null;
        
        if (createJobDialogOpen && selectedJobType) {
          contractorTypeToUse = selectedJobType;
        } else if (sendQuoteDialogOpen && selectedJobForQuote) {
          contractorTypeToUse = selectedJobForQuote.contractorType;
        }
        
        if (contractorTypeToUse) {
          // Get all contractors of the selected type
          const relevantContractors = contractors.filter(c => c.contractorTypeSlug === contractorTypeToUse);
          const emailsToFetch = relevantContractors.map(c => c.email).filter(Boolean);
          
          if (emailsToFetch.length > 0) {
            try {
              setAvailabilityLoading(true);
              const availability = await contractorAvailabilityService.getMultipleContractorsAvailability(
                emailsToFetch as string[],
                jobStartDate || undefined,
                jobFinishDate || undefined
              );
              setContractorAvailability(availability);
            } catch (err) {
              console.error('Error fetching contractor availability:', err);
              // Don't show error to user, just log it
            } finally {
              setAvailabilityLoading(false);
            }
          }
        }
      }
    };

    fetchAvailabilityForContractors();
  }, [createJobDialogOpen, sendQuoteDialogOpen, contractors, selectedJobType, selectedJobForQuote, jobStartDate, jobFinishDate]);

  // Listen for newly created contractor jobs and update list without refresh
  useEffect(() => {
    const handleJobCreated = (e: any) => {
      const detail = e?.detail;
      if (!detail?.job) return;
      // Ensure the event is for this property
      if (detail.propertyId !== propertyId) return;
      setContractorJobs(prev => {
        const exists = prev.some(j => j._id === detail.job._id);
        return exists ? prev : [detail.job, ...prev];
      });
    };
    window.addEventListener('contractorJob:created', handleJobCreated as EventListener);
    return () => {
      window.removeEventListener('contractorJob:created', handleJobCreated as EventListener);
    };
  }, [propertyId]);

  const handleAddContractorClick = () => {
    setNewContractorData({
      name: '',
      email: '',
      phone: '',
      company: '',
      notes: ''
    });
    setAddContractorDialogOpen(true);
  };

  const handleCreateContractor = async () => {
    if (!newContractorData.name || !newContractorData.email || !newContractorData.phone || !selectedJobType) {
      setSnackbar({ open: true, message: 'Name, email, phone, and job type are required', severity: 'error' });
      return;
    }

    try {
      setCreatingContractor(true);
      const payload = {
        name: newContractorData.name,
        email: newContractorData.email,
        phone: newContractorData.phone,
        company: newContractorData.company || undefined,
        notes: newContractorData.notes || undefined,
        contractorTypeSlug: selectedJobType
      };
      
      const response = await api.post(`/agencies/${agencySlug}/contractors`, payload);
      const newContractor = response.data;
      
      // Add to local state
      setContractors(prev => [...prev, newContractor]);
      // Automatically select the newly created contractor
      setCreateJobContractors([newContractor._id]);
      
      setAddContractorDialogOpen(false);
      setSnackbar({ open: true, message: 'Contractor added successfully', severity: 'success' });
    } catch (err: any) {
      console.error('Error creating contractor:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error || 'Failed to create contractor', 
        severity: 'error' 
      });
    } finally {
      setCreatingContractor(false);
    }
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== files.length) {
      setSnackbar({
        open: true,
        message: 'Only image files are allowed',
        severity: 'error'
      });
    }
    
    setJobPhotos(prev => [...prev, ...imageFiles]);
  };

  const handleRemovePhoto = (index: number) => {
    setJobPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Check availability status for a specific contractor
  const checkContractorAvailabilityStatus = (contractorId: string): { 
    status: 'available' | 'conflict' | 'no_availability_set'; 
    message: string;
  } => {
    if (!jobStartDate) {
      return { status: 'no_availability_set', message: 'Date not set' };
    }

    const contractor = contractors.find(c => c._id === contractorId);
    if (!contractor) {
      return { status: 'no_availability_set', message: 'Contractor not found' };
    }

    const contractorSlots = contractorAvailability[contractor.email.toLowerCase()] || [];

    // If no availability is set, allow it
    if (contractorSlots.length === 0) {
      return { status: 'no_availability_set', message: 'No availability set' };
    }

    // For date-only jobs (no time specified)
    if (!jobStartTime || !jobFinishTime) {
      const jobDateStr = jobStartDate;
      
      // Check if contractor has any busy slots on this day
      const busySlots = contractorSlots.filter((slot: any) => !slot.isActive);
      for (const slot of busySlots) {
        const slotStart = new Date(slot.startTime);
        const slotEnd = new Date(slot.endTime);
        const slotStartDateStr = slotStart.toISOString().split('T')[0];
        const slotEndDateStr = slotEnd.toISOString().split('T')[0];
        
        if (slot.isRecurring && slot.recurringPattern?.daysOfWeek) {
          // For recurring slots, check if job day matches
          const jobDate = new Date(jobDateStr);
          const jobDayOfWeek = jobDate.getDay();
          if (slot.recurringPattern.daysOfWeek.includes(jobDayOfWeek)) {
            return {
              status: 'conflict',
              message: 'Has busy slots on this day'
            };
          }
        } else {
          // For one-time slots, check if date matches
          if (jobDateStr >= slotStartDateStr && jobDateStr <= slotEndDateStr) {
            return {
              status: 'conflict',
              message: 'Has busy slots on this day'
            };
          }
        }
      }
      
      // If no busy slots found, the day is available
      return { status: 'available', message: 'Available on this day' };
    }

    // For jobs with time specified, use existing time-based logic
    const jobStart = new Date(`${jobStartDate}T${jobStartTime}:00`);
    const jobFinish = new Date(`${jobFinishDate}T${jobFinishTime}:00`);

    // Check for conflicts with busy slots (isActive: false)
    const busySlots = contractorSlots.filter((slot: any) => !slot.isActive);
    for (const slot of busySlots) {
      const slotStart = new Date(slot.startTime);
      const slotEnd = new Date(slot.endTime);
      
      if (slot.isRecurring && slot.recurringPattern?.daysOfWeek) {
        const jobDayOfWeek = jobStart.getDay();
        if (slot.recurringPattern.daysOfWeek.includes(jobDayOfWeek)) {
          const slotStartTime = slotStart.getHours() * 60 + slotStart.getMinutes();
          const slotEndTime = slotEnd.getHours() * 60 + slotEnd.getMinutes();
          const jobStartTimeMinutes = jobStart.getHours() * 60 + jobStart.getMinutes();
          const jobFinishTimeMinutes = jobFinish.getHours() * 60 + jobFinish.getMinutes();
          
          // Check if times overlap
          if (!(jobFinishTimeMinutes <= slotStartTime || jobStartTimeMinutes >= slotEndTime)) {
            return {
              status: 'conflict',
              message: 'Marked as busy during this time'
            };
          }
        }
      } else {
        // For one-time slots, check if dates match first, then check times
        const slotStartDateStr = slotStart.toISOString().split('T')[0];
        const slotEndDateStr = slotEnd.toISOString().split('T')[0];
        const jobStartDateStr = jobStart.toISOString().split('T')[0];
        const jobFinishDateStr = jobFinish.toISOString().split('T')[0];
        
        // Check if dates overlap (job date falls within slot date range)
        if (jobStartDateStr <= slotEndDateStr && jobFinishDateStr >= slotStartDateStr) {
          // If dates overlap, check if times overlap
          // Extract time components for comparison
          const slotStartTime = slotStart.getHours() * 60 + slotStart.getMinutes();
          const slotEndTime = slotEnd.getHours() * 60 + slotEnd.getMinutes();
          const jobStartTimeMinutes = jobStart.getHours() * 60 + jobStart.getMinutes();
          const jobFinishTimeMinutes = jobFinish.getHours() * 60 + jobFinish.getMinutes();
          
          // Check if times overlap (only if on the same day)
          if (jobStartDateStr === slotStartDateStr && 
              !(jobFinishTimeMinutes <= slotStartTime || jobStartTimeMinutes >= slotEndTime)) {
            return {
              status: 'conflict',
              message: 'Marked as busy during this time'
            };
          }
        }
      }
    }

    // Check if job time overlaps with any available slot (isActive: true)
    const availableSlots = contractorSlots.filter((slot: any) => slot.isActive);
    
    if (availableSlots.length > 0) {
      for (const slot of availableSlots) {
        const slotStart = new Date(slot.startTime);
        const slotEnd = new Date(slot.endTime);
        
        if (slot.isRecurring && slot.recurringPattern?.daysOfWeek) {
          const jobDayOfWeek = jobStart.getDay();
          if (slot.recurringPattern.daysOfWeek.includes(jobDayOfWeek)) {
            const slotStartTime = slotStart.getHours() * 60 + slotStart.getMinutes();
            const slotEndTime = slotEnd.getHours() * 60 + slotEnd.getMinutes();
            const jobStartTimeMinutes = jobStart.getHours() * 60 + jobStart.getMinutes();
            const jobFinishTimeMinutes = jobFinish.getHours() * 60 + jobFinish.getMinutes();
            
            // Check if times overlap
            if (!(jobFinishTimeMinutes <= slotStartTime || jobStartTimeMinutes >= slotEndTime)) {
              return {
                status: 'available',
                message: 'Available during this time'
              };
            }
          }
        } else {
          // For one-time slots, check if dates match first, then check times
          const slotStartDateStr = slotStart.toISOString().split('T')[0];
          const slotEndDateStr = slotEnd.toISOString().split('T')[0];
          const jobStartDateStr = jobStart.toISOString().split('T')[0];
          const jobFinishDateStr = jobFinish.toISOString().split('T')[0];
          
          // Check if dates overlap (job date falls within slot date range)
          if (jobStartDateStr <= slotEndDateStr && jobFinishDateStr >= slotStartDateStr) {
            // Extract time components for comparison
            const slotStartTime = slotStart.getHours() * 60 + slotStart.getMinutes();
            const slotEndTime = slotEnd.getHours() * 60 + slotEnd.getMinutes();
            const jobStartTimeMinutes = jobStart.getHours() * 60 + jobStart.getMinutes();
            const jobFinishTimeMinutes = jobFinish.getHours() * 60 + jobFinish.getMinutes();
            
            // Check if times overlap (only if on the same day or within date range)
            if (jobStartDateStr === slotStartDateStr && 
                !(jobFinishTimeMinutes <= slotStartTime || jobStartTimeMinutes >= slotEndTime)) {
              return {
                status: 'available',
                message: 'Available during this time'
              };
            }
          }
        }
      }
    }

    // If no busy conflict found and no available slot matches, the slot is available (not marked = available)
    return { status: 'available', message: 'Available' };
  };

  // Check if job time conflicts with contractor availability
  const checkContractorAvailabilityConflict = (): { hasConflict: boolean; message: string } => {
    // Only check if contractors are selected and date is provided
    if (createJobContractors.length === 0 || !jobStartDate) {
      return { hasConflict: false, message: '' };
    }

    // For date-only jobs (no time specified)
    if (!jobStartTime || !jobFinishTime) {
      const jobDateStr = jobStartDate;
      
      // Check each selected contractor for busy slots on this day
      for (const contractorId of createJobContractors) {
        const contractor = contractors.find(c => c._id === contractorId);
        if (!contractor) continue;

        const contractorSlots = contractorAvailability[contractor.email.toLowerCase()] || [];
        const busySlots = contractorSlots.filter((slot: any) => !slot.isActive);
        
        for (const slot of busySlots) {
          const slotStart = new Date(slot.startTime);
          const slotEnd = new Date(slot.endTime);
          const slotStartDateStr = slotStart.toISOString().split('T')[0];
          const slotEndDateStr = slotEnd.toISOString().split('T')[0];
          
          if (slot.isRecurring && slot.recurringPattern?.daysOfWeek) {
            const jobDate = new Date(jobDateStr);
            const jobDayOfWeek = jobDate.getDay();
            if (slot.recurringPattern.daysOfWeek.includes(jobDayOfWeek)) {
              return {
                hasConflict: true,
                message: `${contractor.name} is marked as busy on this day.`
              };
            }
          } else {
            if (jobDateStr >= slotStartDateStr && jobDateStr <= slotEndDateStr) {
              return {
                hasConflict: true,
                message: `${contractor.name} is marked as busy on this day.`
              };
            }
          }
        }
      }
      
      // No conflicts found for date-only job
      return { hasConflict: false, message: '' };
    }

    // For jobs with time specified, use existing time-based logic
    if (!jobFinishDate) {
      return { hasConflict: false, message: '' };
    }

    const jobStart = new Date(`${jobStartDate}T${jobStartTime}:00`);
    const jobFinish = new Date(`${jobFinishDate}T${jobFinishTime}:00`);

    // Check each selected contractor
    for (const contractorId of createJobContractors) {
      const contractor = contractors.find(c => c._id === contractorId);
      if (!contractor) continue;

      const contractorSlots = contractorAvailability[contractor.email.toLowerCase()] || [];
      
      // Check for conflicts with busy slots (isActive: false)
      const busySlots = contractorSlots.filter((slot: any) => !slot.isActive);
      for (const slot of busySlots) {
        const slotStart = new Date(slot.startTime);
        const slotEnd = new Date(slot.endTime);
        
        // Check if job time overlaps with busy slot
        if (slot.isRecurring && slot.recurringPattern?.daysOfWeek) {
          // For recurring slots, check if job day matches and time overlaps
          const jobDayOfWeek = jobStart.getDay();
          if (slot.recurringPattern.daysOfWeek.includes(jobDayOfWeek)) {
            // Extract time from dates for comparison
            const slotStartTime = slotStart.getHours() * 60 + slotStart.getMinutes();
            const slotEndTime = slotEnd.getHours() * 60 + slotEnd.getMinutes();
            const jobStartTimeMinutes = jobStart.getHours() * 60 + jobStart.getMinutes();
            const jobFinishTimeMinutes = jobFinish.getHours() * 60 + jobFinish.getMinutes();
            
            // Check if times overlap
            if (!(jobFinishTimeMinutes <= slotStartTime || jobStartTimeMinutes >= slotEndTime)) {
              return {
                hasConflict: true,
                message: `${contractor.name} is marked as busy during the selected time slot.`
              };
            }
          }
        } else {
          // For one-time slots, check if dates match first, then check times
          const slotStartDateStr = slotStart.toISOString().split('T')[0];
          const slotEndDateStr = slotEnd.toISOString().split('T')[0];
          const jobStartDateStr = jobStart.toISOString().split('T')[0];
          const jobFinishDateStr = jobFinish.toISOString().split('T')[0];
          
          // Check if dates overlap (job date falls within slot date range)
          if (jobStartDateStr <= slotEndDateStr && jobFinishDateStr >= slotStartDateStr) {
            // If dates overlap, check if times overlap
            // Extract time components for comparison (use local time since job times are entered in local time)
            const slotStartTime = slotStart.getHours() * 60 + slotStart.getMinutes();
            const slotEndTime = slotEnd.getHours() * 60 + slotEnd.getMinutes();
            const jobStartTimeMinutes = jobStart.getHours() * 60 + jobStart.getMinutes();
            const jobFinishTimeMinutes = jobFinish.getHours() * 60 + jobFinish.getMinutes();
            
            // Check if times overlap (only if on the same day)
            if (jobStartDateStr === slotStartDateStr && 
                !(jobFinishTimeMinutes <= slotStartTime || jobStartTimeMinutes >= slotEndTime)) {
              return {
                hasConflict: true,
                message: `${contractor.name} is marked as busy during the selected time slot.`
              };
            }
          }
        }
      }

      // Check if job time is within any available slot (isActive: true)
      const availableSlots = contractorSlots.filter((slot: any) => slot.isActive);
      
      // If contractor has availability set (either available or busy slots), check if job time falls within any available slot
      // If contractor has no availability set at all, allow the job
      if (contractorSlots.length > 0 && availableSlots.length > 0) {
        let isWithinAvailableSlot = false;
        
        for (const slot of availableSlots) {
          const slotStart = new Date(slot.startTime);
          const slotEnd = new Date(slot.endTime);
          
          if (slot.isRecurring && slot.recurringPattern?.daysOfWeek) {
            // For recurring slots, check if job day matches and time overlaps with slot
            const jobDayOfWeek = jobStart.getDay();
            if (slot.recurringPattern.daysOfWeek.includes(jobDayOfWeek)) {
              // Extract time from dates for comparison
              const slotStartTime = slotStart.getHours() * 60 + slotStart.getMinutes();
              const slotEndTime = slotEnd.getHours() * 60 + slotEnd.getMinutes();
              const jobStartTimeMinutes = jobStart.getHours() * 60 + jobStart.getMinutes();
              const jobFinishTimeMinutes = jobFinish.getHours() * 60 + jobFinish.getMinutes();
              
              // Check if job time overlaps with slot time (not necessarily completely within)
              if (!(jobFinishTimeMinutes <= slotStartTime || jobStartTimeMinutes >= slotEndTime)) {
                isWithinAvailableSlot = true;
                break;
              }
            }
          } else {
            // For one-time slots, check if job overlaps with slot
            if (!(jobFinish <= slotStart || jobStart >= slotEnd)) {
              isWithinAvailableSlot = true;
              break;
            }
          }
        }
        
        // If contractor has availability set but job doesn't overlap with any available slot, it's a conflict
        if (!isWithinAvailableSlot) {
          return {
            hasConflict: true,
            message: `${contractor.name} has not marked the selected time slot as available.`
          };
        }
      }
      // If contractorSlots.length === 0, no availability is set, so allow the job
    }

    return { hasConflict: false, message: '' };
  };

  const handleCreateJob = async () => {
    if (!selectedJobType) return;
    
    // Validate time is provided for auctioner jobs
    if (selectedJobType === 'auctioner' && !jobStartTime) {
      setSnackbar({
        open: true,
        message: 'Start time is required for auctioner jobs',
        severity: 'error'
      });
      return;
    }

    // Check for contractor availability conflicts when contractors are selected
    if (createJobContractors.length > 0 && jobStartDate && jobStartTime && jobFinishDate && jobFinishTime) {
      const conflictCheck = checkContractorAvailabilityConflict();
      if (conflictCheck.hasConflict) {
        setSnackbar({
          open: true,
          message: conflictCheck.message,
          severity: 'error'
        });
        return;
      }
    }

    try {
      setLoading(true);
      setUploadingPhotos(true);
      
      // Upload photos first using the contractor job photo upload endpoint
      const uploadedPhotoUrls: string[] = [];
      for (const photo of jobPhotos) {
        try {
          // Use the contractor job photo upload endpoint which stores photos separately from property images
          const response = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/contractor-job-photos`, {
            fileName: photo.name,
            mimeType: photo.type || 'application/octet-stream',
            sizeBytes: photo.size
          });
          
          const { uploadUrl, key } = response.data;
          
          // Upload file directly to storage using presigned URL
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': photo.type || 'application/octet-stream' },
            body: photo
          });
          
          // Finalize upload to record size and update storage usage
          await api.post(`/agencies/${agencySlug}/properties/${propertyId}/contractor-job-photos/finalize`, {
            key,
            sizeBytes: photo.size
          });
          
          // Get the display URL
          const url = response.data.url;
          uploadedPhotoUrls.push(url);
        } catch (error) {
          console.error('Error uploading photo:', error);
          throw new Error(`Failed to upload photo: ${photo.name}`);
        }
      }
      
      // Combine date and time for auctioner jobs or when editing with times
      let startDateWithTime = jobStartDate || undefined;
      let finishDateWithTime = jobFinishDate || undefined;
      
      // For auctioner jobs, always combine time if provided
      // For edit mode, combine time if it was set (either originally or by user)
      if (jobStartDate && jobStartTime) {
        if (selectedJobType === 'auctioner' || editMode) {
          // Combine date and time: YYYY-MM-DDTHH:MM:SS format
          startDateWithTime = `${jobStartDate}T${jobStartTime}:00`;
        }
      }
      
      if (jobFinishDate && jobFinishTime) {
        if (selectedJobType === 'auctioner' || editMode) {
          finishDateWithTime = `${jobFinishDate}T${jobFinishTime}:00`;
        }
      }
      
      // Create or update the job with photos
      if (editMode && editingJobId) {
        // Combine existing photos with newly uploaded ones
        const allPhotos = [...existingPhotos.filter((p: any) => !removedPhotoIds.includes(p._id || '')), ...uploadedPhotoUrls.map((url: string) => ({ url }))];
        
        await api.put(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs/${editingJobId}`, {
          contractorType: selectedJobType,
          notes: jobNotes,
          startDate: startDateWithTime,
          finishDate: finishDateWithTime,
          photos: allPhotos.map((p: any) => p.url || p)
        });
      } else {
        const payload: any = {
          contractorType: selectedJobType,
          notes: jobNotes,
          photos: uploadedPhotoUrls,
          startDate: startDateWithTime,
          finishDate: finishDateWithTime
        };
        
        // If sending quotes at the same time, include quote parameters
        if (sendQuotesOnCreate && createJobContractors.length > 0 && createJobQuoteAmount) {
          payload.contractorIds = createJobContractors;
          payload.amount = parseFloat(createJobQuoteAmount);
          payload.quoteNotes = createJobQuoteNotes || undefined;
        }
        
        await api.post(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`, payload);
      }
      
      setSnackbar({
        open: true,
        message: editMode 
          ? 'Contractor job updated successfully!'
          : (sendQuotesOnCreate && createJobContractors.length > 0 
            ? 'Contractor job created and quote invitations sent successfully!'
            : 'Contractor job created successfully!'),
        severity: 'success'
      });
      
      setCreateJobDialogOpen(false);
      setEditMode(false);
      setEditingJobId(null);
      setSelectedJobType('');
      setJobNotes('');
      setJobStartDate('');
      setJobFinishDate('');
      setJobStartTime('');
      setJobFinishTime('');
      setJobPhotos([]);
      setSendQuotesOnCreate(false);
      setCreateJobContractors([]);
      setCreateJobQuoteAmount('');
      setCreateJobQuoteNotes('');
      setSelectedAvailabilitySlot(null);
      setOpenSchedule(null);
      fetchContractorJobs(); // Refresh the list
    } catch (err: any) {
      console.error('Error creating/updating contractor job:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || `Failed to ${editMode ? 'update' : 'create'} contractor job`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setUploadingPhotos(false);
    }
  };

  const handleSendQuotes = async () => {
    if (!selectedJobForQuote || selectedContractors.length === 0) return;
    
    try {
      setLoading(true);
      // Send agency quote with contractorIds (backend will email invites)
      const amount = sendQuoteAmount ? parseFloat(sendQuoteAmount) : 0;
      await agencySendQuote(agencySlug, selectedJobForQuote._id, { amount, notes: sendQuoteNotes || undefined, contractorIds: selectedContractors as any });
      
      setSnackbar({
        open: true,
        message: 'Quote invitations sent successfully!',
        severity: 'success'
      });
      
      setSendQuoteDialogOpen(false);
      setSelectedJobForQuote(null);
      setSelectedContractors([]);
      setSendQuoteAmount('');
      setSendQuoteNotes('');
      fetchContractorJobs(); // Refresh the list
    } catch (err: any) {
      console.error('Error sending quotes:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to send quotes',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const openCancelJobConfirm = (job: ContractorJob) => {
    setPendingCancelJob(job);
    setConfirmDialogConfig({
      title: 'Cancel Contractor Job',
      message: `Are you sure you want to cancel the job for ${job.contractorName}? This action cannot be undone.`,
      confirmText: 'Cancel Job',
      confirmColor: 'error'
    });
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    setPendingCancelJob(null);
    setConfirmDialogConfig(null);
  };

  const handleConfirmDialogConfirm = async () => {
    if (!pendingCancelJob) return;
    try {
      setLoading(true);
      
      // Check if this is a delete or cancel action based on the dialog config
      if (confirmDialogConfig?.confirmText === 'Delete Job') {
        // Delete job
        await deleteJob(agencySlug, pendingCancelJob._id);
        setSnackbar({ open: true, message: 'Job deleted successfully', severity: 'success' });
      } else {
        // Cancel job
        await api.delete(`/agencies/${agencySlug}/jobs/${pendingCancelJob._id}/cancel`);
        setSnackbar({ open: true, message: 'Contractor job cancelled successfully!', severity: 'success' });
      }
      
      fetchContractorJobs();
    } catch (err: any) {
      console.error('Error processing job action:', err);
      const action = confirmDialogConfig?.confirmText === 'Delete Job' ? 'delete' : 'cancel';
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error || `Failed to ${action} contractor job`, 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
      handleConfirmDialogClose();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getJobTypeIcon = (contractorType: string) => {
    const contractor = contractors.find(c => c.contractorTypeSlug === contractorType);
    return contractor?.contractorTypeIcon || '🔧';
  };

  const getContractorTypeName = (contractorType: string) => {
    const type = contractorTypes.find(t => t.slug === contractorType);
    if (type) return type.name;
    // Fallback: convert slug to title case (e.g., "property-sign-board-maker" -> "Property Sign Board Maker")
    return contractorType
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleViewJobDetails = (job: ContractorJob) => {
    setSelectedJob(job);
    setJobDetailsOpen(true);
  };

  // When job details dialog opens, refresh the selected job to include latest uploaded files
  useEffect(() => {
    const refreshSelectedJob = async () => {
      if (!jobDetailsOpen || !selectedJob) return;
      try {
        const response = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
        const list: ContractorJob[] = response.data || [];
        const latest = list.find(j => j._id === selectedJob._id);
        if (latest) setSelectedJob(latest);
      } catch (err) {
        // ignore refresh error; keep existing snapshot
      }
    };
    refreshSelectedJob();
  }, [jobDetailsOpen, selectedJob?._id, agencySlug, propertyId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const groupedJobs = contractorJobs.reduce((acc, job) => {
    if (!acc[job.contractorType]) {
      acc[job.contractorType] = [];
    }
    acc[job.contractorType].push(job);
    return acc;
  }, {} as Record<string, ContractorJob[]>);

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <WorkIcon />
              <Typography variant="h6">Contractor Jobs</Typography>
              {contractorJobs.length > 0 && (
                <Chip 
                  label={`${contractorJobs.length} job${contractorJobs.length !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
            {isAdmin && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => { setEditMode(false); setEditingJobId(null); setExistingPhotos([]); setRemovedPhotoIds([]); setCreateJobDialogOpen(true); }}
              >
                Create New Job
              </Button>
            )}
          </Box>

          {/* Summary removed on contractor-jobs page; summary now shown on Property Details page */}
            {loading && (
              <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography>Loading contractor jobs...</Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!loading && contractorJobs.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary" gutterBottom>
                  No contractor jobs assigned to this property yet.
                </Typography>
                {isAdmin && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateJobDialogOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Create Contractor Job
                  </Button>
                )}
              </Box>
            )}

            {!loading && contractorJobs.length > 0 && (
              <Box>

                {Object.entries(groupedJobs).map(([contractorType, jobs]) => (
                  <Box key={contractorType} mb={3}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      {getJobTypeIcon(contractorType)} {getContractorTypeName(contractorType)} Jobs
                    </Typography>
                    
                    {jobs.map((job) => (
                      <JobWithResponses
                        key={job._id}
                        job={job}
                        contractorTypeName={getContractorTypeName(job.contractorType)}
                        onAcceptQuote={handleAcceptQuote}
                        onDeclineQuote={handleDeclineQuote}
                        onViewJobDetails={handleViewJobDetails}
                        onCancelJob={openCancelJobConfirm}
                        onSendQuotes={openSendQuotesDialog}
                        onDeleteJob={handleDeleteJob}
                        onEditJob={(job) => {
                          const qs = (job as any).quoteStatus;
                          if (qs === 'accepted') return; // Block editing if quote is accepted
                          setEditMode(true);
                          setEditingJobId(job._id);
                          setSelectedJobType(job.contractorType || '');
                          setJobNotes((job as any).notes || '');
                          const startDate = (job as any).startDate;
                          const finishDate = (job as any).finishDate;
                          setJobStartDate(toInputDate(startDate));
                          setJobFinishDate(toInputDate(finishDate));
                          // Extract time if it exists in the date string
                          const startTime = toInputTime(startDate);
                          const finishTime = toInputTime(finishDate);
                          setJobStartTime(startTime);
                          setJobFinishTime(finishTime);
                          setJobPhotos([]);
                          setExistingPhotos(Array.isArray((job as any).photos) ? (job as any).photos : []);
                          setRemovedPhotoIds([]);
                          setCreateJobDialogOpen(true);
                        }}
                        isAdmin={isAdmin}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            )}

            {/* Quote Management Section - show when there is data */}
            {propertyQuotes && propertyQuotes.length > 0 && (
              <Box mt={3}>
                <QuoteManagement
                  jobQuotes={propertyQuotes}
                  onAcceptQuote={handleAcceptQuote}
                  onDeclineQuote={handleDeclineQuote}
                  onViewQuoteDetails={handleViewQuoteDetails}
                  loading={quotesLoading}
                />
              </Box>
            )}
        </CardContent>
      </Card>

      {/* Job Details Dialog */}
      <Dialog 
        open={jobDetailsOpen} 
        onClose={() => setJobDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Job Details{selectedJob ? ` — ${selectedJob.contractorName}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {!selectedJob ? (
            <Typography variant="body2" color="text.secondary">No job selected</Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Typography variant="body1">{selectedJob.contractorType?.replace('_', ' ')}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip size="small" label={selectedJob.status.replace('_', ' ')} color={getStatusColor(selectedJob.status) as any} />
                </Grid>
                {selectedJob.dueDate && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Due</Typography>
                    <Typography variant="body1">{new Date(selectedJob.dueDate).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                  </Grid>
                )}
              </Grid>

              {(selectedJob as any).notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                  <Typography variant="body2">{(selectedJob as any).notes}</Typography>
                </Box>
              )}

              <Box>
                <Typography variant="h6" gutterBottom>Photos</Typography>
                {selectedJob.photos && selectedJob.photos.length > 0 ? (
                  <List>
                    {selectedJob.photos.map((p, idx) => (
                      <ListItem 
                        key={idx} 
                        button
                        onClick={async () => {
                          const id = (p as any)._id;
                          if (!id) return;
                          const url = `/api/agencies/${agencySlug}/job-files/${id}`;
                          try { await downloadFileAsBlob(url, p.filename); } catch {}
                        }}
                      >
                        <ListItemText primary={p.filename} secondary={new Date(p.uploadedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No photos uploaded</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom>Floor Plans</Typography>
                {selectedJob.floorPlans && selectedJob.floorPlans.length > 0 ? (
                  <List>
                    {selectedJob.floorPlans.map((p, idx) => (
                      <ListItem 
                        key={idx} 
                        button
                        onClick={async () => {
                          const id = (p as any)._id;
                          if (!id) return;
                          const url = `/api/agencies/${agencySlug}/job-files/${id}`;
                          try { await downloadFileAsBlob(url, p.filename); } catch {}
                        }}
                      >
                        <ListItemText primary={p.filename} secondary={new Date(p.uploadedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No floor plans uploaded</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom>Invoices</Typography>
                {selectedJob.invoices && selectedJob.invoices.length > 0 ? (
                  <List>
                    {selectedJob.invoices.map((inv, idx) => (
                      <ListItem 
                        key={idx} 
                        button
                        onClick={async () => {
                          const id = (inv as any)._id;
                          if (!id) return;
                          const url = `/api/agencies/${agencySlug}/job-files/${id}`;
                          try { await downloadFileAsBlob(url, inv.filename || 'invoice'); } catch {}
                        }}
                      >
                        <ListItemText primary={`${inv.filename}${inv.amount ? ` - $${inv.amount}` : ''}`} secondary={new Date(inv.uploadedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No invoices uploaded</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom>Attachments</Typography>
                {selectedJob.attachments && selectedJob.attachments.length > 0 ? (
                  <List>
                    {selectedJob.attachments.map((att, idx) => (
                      <ListItem 
                        key={idx} 
                        button
                        onClick={async () => {
                          const id = (att as any)._id;
                          if (!id) return;
                          const url = `/api/agencies/${agencySlug}/job-files/${id}`;
                          try { await downloadFileAsBlob(url, att.filename || 'attachment'); } catch {}
                        }}
                      >
                        <ListItemText primary={att.filename} secondary={new Date(att.uploadedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No attachments uploaded</Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={async () => {
            if (!selectedJob) return;
            try {
              const response = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
              const list: ContractorJob[] = response.data || [];
              const latest = list.find(j => j._id === selectedJob._id);
              if (latest) setSelectedJob(latest);
            } catch {}
          }}>Refresh</Button>
          <Button onClick={() => setJobDetailsOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Job Confirmation Dialog */}
      <Dialog 
        open={confirmDialogOpen} 
        onClose={handleConfirmDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {confirmDialogConfig?.confirmColor === 'success' ? (
            <CheckCircleIcon color="success" />
          ) : confirmDialogConfig?.confirmColor === 'error' ? (
            <DeleteIcon color="error" />
          ) : (
            <AssignmentIcon color="primary" />
          )}
          {confirmDialogConfig?.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {confirmDialogConfig?.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleConfirmDialogClose}
            variant="outlined"
          >
            {confirmDialogConfig?.confirmText === 'Delete Job' ? 'Cancel' : 'Keep Job'}
          </Button>
          <Button 
            onClick={handleConfirmDialogConfirm}
            variant="contained"
            color={confirmDialogConfig?.confirmColor || 'primary'}
          >
            {confirmDialogConfig?.confirmText}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Quote Dialog */}
      <Dialog open={quoteDialogOpen} onClose={() => setQuoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Contractor Job Quote</DialogTitle>
        <DialogContent>
          {quoteJob && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {(quoteJob as any).agencyQuote && (
                <Alert severity="info">
                  Last agency offer: {(quoteJob as any).agencyQuote.amount} {((quoteJob as any).agencyQuote.notes ? `— ${(quoteJob as any).agencyQuote.notes}` : '')}
                </Alert>
              )}
              {(quoteJob as any).contractorQuote && (
                <Alert severity="info">
                  Contractor response: {(quoteJob as any).contractorQuote.amount} {((quoteJob as any).contractorQuote.notes ? `— ${(quoteJob as any).contractorQuote.notes}` : '')}
                </Alert>
              )}
              <TextField
                label="Offer amount"
                type="number"
                value={quoteAmount}
                onChange={(e) => setQuoteAmount(e.target.value)}
                fullWidth
              />
              <TextField
                label="Notes"
                value={quoteNotes}
                onChange={(e) => setQuoteNotes(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {quoteJob && (quoteJob as any).quoteStatus && (quoteJob as any).quoteStatus !== 'none' && (quoteJob as any).quoteStatus !== 'accepted' && (
            <Button
              color="success"
              onClick={async () => {
                if (!quoteJob) return;
                try {
                  await agencyAcceptQuote(agencySlug, quoteJob._id);
                  await fetchContractorJobs();
                  setQuoteDialogOpen(false);
                } catch (e) {
                  setSnackbar({ open: true, message: 'Failed to approve quote', severity: 'error' });
                }
              }}
            >
              Approve
            </Button>
          )}
          {quoteJob && (quoteJob as any).quoteStatus && (quoteJob as any).quoteStatus !== 'accepted' && (
            <Button
              color="error"
              onClick={async () => {
                if (!quoteJob) return;
                try {
                  await agencyDeclineQuote(agencySlug, quoteJob._id, quoteNotes || undefined);
                  await fetchContractorJobs();
                  setQuoteDialogOpen(false);
                } catch (e) {
                  setSnackbar({ open: true, message: 'Failed to decline quote', severity: 'error' });
                }
              }}
            >
              Decline
            </Button>
          )}
          <Button onClick={() => setQuoteDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            disabled={quoteSubmitting || !quoteAmount}
            onClick={async () => {
              if (!quoteJob) return;
              try {
                setQuoteSubmitting(true);
                const fn = ((quoteJob as any).quoteStatus && (quoteJob as any).quoteStatus !== 'none') ? agencyCounterQuote : agencySendQuote;
                await fn(agencySlug, quoteJob._id, { amount: parseFloat(quoteAmount), notes: quoteNotes || undefined });
                await fetchContractorJobs();
                setQuoteDialogOpen(false);
              } catch (e) {
                setSnackbar({ open: true, message: 'Failed to send quote', severity: 'error' });
              } finally {
                setQuoteSubmitting(false);
              }
            }}
          >
            {quoteSubmitting ? 'Sending...' : ((quoteJob as any)?.quoteStatus && (quoteJob as any).quoteStatus !== 'none' ? 'Counter Offer' : 'Send Quote')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Contractor Job Dialog */}
      <Dialog open={createJobDialogOpen} onClose={() => { 
        setCreateJobDialogOpen(false); 
        setEditMode(false); 
        setEditingJobId(null);
        setSendQuotesOnCreate(false);
        setCreateJobContractors([]);
        setCreateJobQuoteAmount('');
        setCreateJobQuoteNotes('');
        setJobStartTime('');
        setJobFinishTime('');
        setOpenSchedule(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Contractor Job' : 'Create Contractor Job'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Job Type</InputLabel>
              <Select
                value={selectedJobType}
                onChange={(e) => setSelectedJobType(e.target.value)}
                label="Job Type"
              >
                {jobTypeOptions.length === 0 ? (
                  <MenuItem value="" disabled>No contractor types available</MenuItem>
                ) : (
                  jobTypeOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Job Notes"
              multiline
              rows={3}
              value={jobNotes}
              onChange={(e) => setJobNotes(e.target.value)}
              placeholder="Describe the job requirements..."
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Start Date (Optional)"
                type="date"
                value={jobStartDate}
                onChange={(e) => setJobStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Finish Date (Optional)"
                type="date"
                value={jobFinishDate}
                onChange={(e) => setJobFinishDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            {/* Time fields for auctioner jobs or when editing a job that originally had times */}
            {(selectedJobType === 'auctioner' || (editMode && (jobStartTime || jobFinishTime))) && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={selectedJobType === 'auctioner' ? 'Start Time (Required for Auctioner)' : 'Start Time (Optional)'}
                  type="time"
                  value={jobStartTime}
                  onChange={(e) => setJobStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required={selectedJobType === 'auctioner'}
                  helperText={selectedJobType === 'auctioner' ? 'Time when the auction starts' : 'Time when the job starts'}
                />
                <TextField
                  fullWidth
                  label="Finish Time (Optional)"
                  type="time"
                  value={jobFinishTime}
                  onChange={(e) => setJobFinishTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Estimated finish time"
                />
              </Box>
            )}
            
            {/* Existing Photos (Edit mode) */}
            {editMode && existingPhotos && existingPhotos.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Existing Photos</Typography>
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  {existingPhotos.map((p, idx) => (
                    <Grid item xs={6} sm={4} md={3} key={(p as any)._id || idx}>
                      <Card sx={{ position: 'relative' }}>
                        <CardMedia component="img" height="100" image={p.url || ''} alt={p.filename || 'Job photo'} />
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
                          onClick={() => {
                            const id = (p as any)._id;
                            if (id) setRemovedPhotoIds(prev => [...prev, String(id)]);
                            setExistingPhotos(prev => prev.filter((_, i) => i !== idx));
                          }}
                        >
                          <DeletePhotoIcon fontSize="small" />
                        </IconButton>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Photo Upload Section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Photos (Optional)
              </Typography>
              
              {/* Upload Button */}
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="job-photo-upload"
                multiple
                type="file"
                onChange={handlePhotoUpload}
              />
              <label htmlFor="job-photo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PhotoCameraIcon />}
                  disabled={uploadingPhotos}
                >
                  Add Photos
                </Button>
              </label>
              
              {/* Photo Preview Grid */}
              {jobPhotos.length > 0 && (
                <Grid container spacing={1} sx={{ mt: 2 }}>
                  {jobPhotos.map((photo, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Card sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="100"
                          image={URL.createObjectURL(photo)}
                          alt={`Job photo ${index + 1}`}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.7)'
                            }
                          }}
                          onClick={() => handleRemovePhoto(index)}
                        >
                          <DeletePhotoIcon fontSize="small" />
                        </IconButton>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              
              {/* Upload Progress */}
              {uploadingPhotos && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Uploading photos...
                  </Typography>
                  <LinearProgress />
                </Box>
              )}
            </Box>

            {/* Option to send quotes when creating job */}
            {!editMode && (
              <>
                <Box sx={{ pt: 2, borderTop: '1px solid #e0e0e0' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendQuotesOnCreate}
                        onChange={(e) => setSendQuotesOnCreate(e.target.checked)}
                      />
                    }
                    label="Send quotes to contractors"
                  />
                </Box>

                {sendQuotesOnCreate && (
                  <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Select Contractor</InputLabel>
                      <Select
                        value={createJobContractors.length > 0 ? createJobContractors[0] : ''}
                        onChange={(e) => {
                          const value = e.target.value as string;
                          // Only allow one contractor at a time
                          setCreateJobContractors(value ? [value] : []);
                        }}
                        label="Select Contractor"
                        disabled={!selectedJobType}
                      >
                        {selectedJobType && contractors
                          .filter(contractor => contractor.contractorTypeSlug === selectedJobType)
                          .map((contractor) => {
                            // Filter out busy slots - only count available slots
                            const availability = (contractorAvailability[contractor.email.toLowerCase()] || []).filter((slot: any) => slot.isActive !== false);
                            const hasAvailability = availability.length > 0;
                            const isSelected = createJobContractors.includes(contractor._id);
                            return (
                              <MenuItem key={contractor._id} value={contractor._id}>
                                <Box sx={{ width: '100%' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography>{contractor.name} ({contractor.email})</Typography>
                                      {hasAvailability && (
                                        <Chip 
                                          label={`${availability.length} slot${availability.length !== 1 ? 's' : ''}`}
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                    {isSelected && (
                                      <CheckCircleIcon color="primary" fontSize="small" />
                                    )}
                                  </Box>
                                  {hasAvailability && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      Available: {availability.slice(0, 2).map((slot: any) => 
                                        new Date(slot.startTime).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                      ).join(', ')}
                                      {availability.length > 2 && '...'}
                                    </Typography>
                                  )}
                                </Box>
                              </MenuItem>
                            );
                          })}
                        {selectedJobType && contractors.filter(c => c.contractorTypeSlug === selectedJobType).length === 0 && (
                          <MenuItem disabled>No contractors available</MenuItem>
                        )}
                        {!selectedJobType && (
                          <MenuItem disabled>Please select a job type first</MenuItem>
                        )}
                      </Select>
                    </FormControl>

                    {/* Contractor Availability Calendar */}
                    {createJobContractors.length > 0 && (
                      <Box sx={{ pl: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
                          Contractor Availability Calendar
                        </Typography>
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, bgcolor: '#f9f9f9' }}>
                          {/* Show selected day calendar when date is set */}
                          {jobStartDate && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  Selected Day
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const currentDate = new Date(jobStartDate);
                                      currentDate.setDate(currentDate.getDate() - 1);
                                      const newDateStr = currentDate.toISOString().split('T')[0];
                                      setJobStartDate(newDateStr);
                                      if (jobFinishDate) {
                                        const finishDate = new Date(jobFinishDate);
                                        finishDate.setDate(finishDate.getDate() - 1);
                                        setJobFinishDate(finishDate.toISOString().split('T')[0]);
                                      }
                                    }}
                                    sx={{ 
                                      border: '1px solid #e0e0e0',
                                      '&:hover': { bgcolor: '#f5f5f5' }
                                    }}
                                  >
                                    <ChevronLeftIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const currentDate = new Date(jobStartDate);
                                      currentDate.setDate(currentDate.getDate() + 1);
                                      const newDateStr = currentDate.toISOString().split('T')[0];
                                      setJobStartDate(newDateStr);
                                      if (jobFinishDate) {
                                        const finishDate = new Date(jobFinishDate);
                                        finishDate.setDate(finishDate.getDate() + 1);
                                        setJobFinishDate(finishDate.toISOString().split('T')[0]);
                                      }
                                    }}
                                    sx={{ 
                                      border: '1px solid #e0e0e0',
                                      '&:hover': { bgcolor: '#f5f5f5' }
                                    }}
                                  >
                                    <ChevronRightIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Box sx={{ 
                                  width: 60, 
                                  height: 60, 
                                  borderRadius: 1, 
                                  bgcolor: '#1976d2', 
                                  color: 'white',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold'
                                }}>
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
                                    {new Date(jobStartDate).toLocaleDateString('en-AU', { month: 'short' })}
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontSize: '1.5rem', lineHeight: 1, fontWeight: 'bold' }}>
                                    {new Date(jobStartDate).getDate()}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>
                                    {new Date(jobStartDate).toLocaleDateString('en-AU', { weekday: 'short' })}
                                  </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {new Date(jobStartDate).toLocaleDateString('en-AU', { 
                                      weekday: 'long', 
                                      month: 'long', 
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </Typography>
                                  
                                  {/* Time slot controls - only show if times are set, otherwise show date-only message */}
                                  {jobStartTime && jobFinishTime ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                      <TextField
                                        label="Start Time"
                                        type="time"
                                        value={jobStartTime}
                                        onChange={(e) => setJobStartTime(e.target.value)}
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 140 }}
                                      />
                                      <Typography variant="body2" color="text.secondary">
                                        to
                                      </Typography>
                                      <TextField
                                        label="Finish Time"
                                        type="time"
                                        value={jobFinishTime || ''}
                                        onChange={(e) => setJobFinishTime(e.target.value)}
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 140 }}
                                      />
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      Date only (no time specified)
                                    </Typography>
                                  )}
                                  
                                  {/* Show availability status for each contractor */}
                                  {createJobContractors.length > 0 && (
                                      <Box sx={{ mt: 1.5 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {createJobContractors.map((contractorId) => {
                                            const contractor = contractors.find(c => c._id === contractorId);
                                            if (!contractor) return null;
                                            
                                            const availabilityStatus = checkContractorAvailabilityStatus(contractorId);
                                            
                                            return (
                                              <Box key={contractorId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                  label={availabilityStatus.status === 'available' ? '✓ Available' : 
                                                         availabilityStatus.status === 'conflict' ? '✗ Conflict' : 
                                                         '○ No availability set'}
                                                  size="small"
                                                  color={availabilityStatus.status === 'available' ? 'success' : 
                                                         availabilityStatus.status === 'conflict' ? 'error' : 
                                                         'default'}
                                                  variant={availabilityStatus.status === 'no_availability_set' ? 'outlined' : 'filled'}
                                                  sx={{ height: 24, fontSize: '0.7rem' }}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                  {contractor.name}: {availabilityStatus.message}
                                                </Typography>
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                        
                                        {/* Show hint when there are conflicts */}
                                        {createJobContractors.some((contractorId) => {
                                          const status = checkContractorAvailabilityStatus(contractorId);
                                          return status.status === 'conflict';
                                        }) && (
                                          <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.75rem' }}>
                                            <Typography variant="caption">
                                              Use the navigation arrows above to find a day with available slots for all contractors.
                                            </Typography>
                                          </Alert>
                                        )}
                                        
                                        {/* Show button to set job time/day when all contractors are available */}
                                        {createJobContractors.length > 0 && 
                                         createJobContractors.every((contractorId) => {
                                           const status = checkContractorAvailabilityStatus(contractorId);
                                           return status.status === 'available';
                                         }) && (
                                          <Box sx={{ mt: 1.5 }}>
                                            <Button
                                              variant="contained"
                                              color="success"
                                              size="small"
                                              onClick={() => {
                                                // The dates and times are already set in the form fields
                                                // This button confirms the selection
                                                const message = jobStartTime && jobFinishTime 
                                                  ? 'Job time slot confirmed for all contractors'
                                                  : 'Job date confirmed for all contractors';
                                                setSnackbar({
                                                  open: true,
                                                  message: message,
                                                  severity: 'success'
                                                });
                                              }}
                                              sx={{ fontSize: '0.75rem' }}
                                            >
                                              ✓ Confirm This {jobStartTime && jobFinishTime ? 'Time Slot' : 'Date'}
                                            </Button>
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                        {selectedJobType && (
                          <Box sx={{ pl: 4 }}>
                            <Button 
                              startIcon={<AddIcon />}
                              onClick={handleAddContractorClick}
                              variant="outlined"
                              color="primary"
                              size="small"
                            >
                              Add New Contractor
                            </Button>
                          </Box>
                        )}

                    <TextField
                      label="Offer Amount"
                      type="number"
                      value={createJobQuoteAmount}
                      onChange={(e) => setCreateJobQuoteAmount(e.target.value)}
                      fullWidth
                      inputProps={{ step: '0.01', min: '0' }}
                      helperText="Amount to offer contractors for this job"
                    />

                    <TextField
                      label="Quote Notes (Optional)"
                      value={createJobQuoteNotes}
                      onChange={(e) => setCreateJobQuoteNotes(e.target.value)}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Add any additional notes for the quote..."
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateJobDialogOpen(false);
            setJobPhotos([]);
            setJobStartDate('');
            setJobFinishDate('');
            setJobStartTime('');
            setJobFinishTime('');
            setSendQuotesOnCreate(false);
            setCreateJobContractors([]);
            setCreateJobQuoteAmount('');
            setCreateJobQuoteNotes('');
            setSelectedAvailabilitySlot(null);
          }}>Cancel</Button>
          <Button 
            onClick={handleCreateJob} 
            variant="contained"
            disabled={
              !selectedJobType || 
              loading || 
              // Disable if there are conflicts when contractors are selected and dates are set
              (createJobContractors.length > 0 && jobStartDate && checkContractorAvailabilityConflict().hasConflict)
            }
          >
            {loading ? (editMode ? 'Saving...' : (sendQuotesOnCreate ? 'Creating & Sending...' : 'Creating...')) : (editMode ? 'Save Changes' : 'Create Job')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Quotes Dialog */}
      <Dialog open={sendQuoteDialogOpen} onClose={() => setSendQuoteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Quotes to Contractors</DialogTitle>
        <DialogContent>
          {selectedJobForQuote && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Job: {selectedJobForQuote.contractorType} - {selectedJobForQuote.notes || 'No notes'}
              </Typography>
              
              <FormControl fullWidth>
                <InputLabel>Select Contractors</InputLabel>
                <Select
                  multiple
                  value={selectedContractors}
                  onChange={(e) => setSelectedContractors(e.target.value as string[])}
                  label="Select Contractors"
                >
                  {contractors
                    .filter(contractor => contractor.contractorTypeSlug === selectedJobForQuote.contractorType)
                    .map((contractor) => {
                      // Filter out busy slots - only count available slots
                      const availability = (contractorAvailability[contractor.email.toLowerCase()] || []).filter((slot: any) => slot.isActive !== false);
                      const hasAvailability = availability.length > 0;
                      return (
                        <MenuItem key={contractor._id} value={contractor._id}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography>{contractor.name} ({contractor.email})</Typography>
                              {hasAvailability && (
                                <Chip 
                                  label={`${availability.length} slot${availability.length !== 1 ? 's' : ''}`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            {hasAvailability && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Available: {availability.slice(0, 2).map((slot: any) => 
                                  new Date(slot.startTime).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                ).join(', ')}
                                {availability.length > 2 && '...'}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>

              <TextField
                label="Offer amount"
                type="number"
                value={sendQuoteAmount}
                onChange={(e) => setSendQuoteAmount(e.target.value)}
                fullWidth
              />

              <TextField
                label="Notes (optional)"
                value={sendQuoteNotes}
                onChange={(e) => setSendQuoteNotes(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendQuoteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSendQuotes} 
            variant="contained"
            disabled={selectedContractors.length === 0 || loading}
          >
            {loading ? 'Sending...' : `Send Quotes (${selectedContractors.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Contractor Dialog */}
      <Dialog open={addContractorDialogOpen} onClose={() => setAddContractorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Contractor</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Contractor will be added for <strong>{contractorTypes.find(t => t.slug === selectedJobType)?.name || 'selected job type'}</strong>
            </Alert>

            <TextField
              label="Name"
              value={newContractorData.name}
              onChange={(e) => setNewContractorData({ ...newContractorData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Email"
              type="email"
              value={newContractorData.email}
              onChange={(e) => setNewContractorData({ ...newContractorData, email: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Phone"
              value={newContractorData.phone}
              onChange={(e) => setNewContractorData({ ...newContractorData, phone: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Company (Optional)"
              value={newContractorData.company}
              onChange={(e) => setNewContractorData({ ...newContractorData, company: e.target.value })}
              fullWidth
            />

            <TextField
              label="Notes (Optional)"
              value={newContractorData.notes}
              onChange={(e) => setNewContractorData({ ...newContractorData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddContractorDialogOpen(false)} disabled={creatingContractor}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateContractor} 
            variant="contained"
            disabled={creatingContractor || !newContractorData.name || !newContractorData.email || !newContractorData.phone}
          >
            {creatingContractor ? 'Creating...' : 'Create Contractor'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PropertyContractorJobs;
